import { useState, useEffect, useCallback, useMemo } from "react";
import {
  fetchApplications,
  resolveApplication,
  applicationToPlayer,
  nextPlayerId,
  ageFromDob,
} from "../utils/applications";
import { TIER_COLORS } from "../utils/tiers";

// The public sign-up link, hardcoded to the live domain rather than read off
// window.location: this gets pasted into emails and texts to members, so it
// has to be the address they can actually reach even when a coach copies it
// from localhost or a preview build.
const JOIN_URL = "https://kac-tennis-programming.com/?join";

// Coach-facing review queue for submissions from the public sign-up form
// (/?join). Approving one adds a player to the live roster with no group
// assigned, so it lands in the Roster as unassigned and can be placed like any
// other player. Declining keeps the record but takes it out of the queue.
export default function ApplicationsView({ players, addPlayers, onNotify, onPendingChange }) {
  const [apps, setApps] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  async function copyJoinLink() {
    try {
      // Only available on https and localhost; the fallback covers a coach
      // reaching the app over plain http on the club's network.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(JOIN_URL);
      } else {
        const field = document.createElement("textarea");
        field.value = JOIN_URL;
        field.setAttribute("readonly", "");
        field.style.position = "fixed";
        field.style.opacity = "0";
        document.body.appendChild(field);
        field.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(field);
        if (!ok) throw new Error("copy rejected");
      }
      setCopied(true);
    } catch {
      onNotify?.({ message: "Couldn't copy automatically — select the link and copy it by hand." });
    }
  }

  const load = useCallback(async () => {
    try {
      const rows = await fetchApplications();
      setApps(rows);
      setError(null);
    } catch {
      setApps([]);
      setError("Could not load sign-ups. The server may be unreachable.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, declined: 0 };
    (apps || []).forEach(a => {
      const s = a.status || "pending";
      if (c[s] !== undefined) c[s]++;
    });
    return c;
  }, [apps]);

  // Keep the tab badge in step with what's actually in the queue, including
  // after an approve or decline resolves.
  useEffect(() => {
    if (apps !== null) onPendingChange?.(counts.pending);
  }, [apps, counts.pending, onPendingChange]);

  const visible = useMemo(
    () =>
      (apps || [])
        .filter(a => (a.status || "pending") === filter)
        .slice()
        .reverse(),
    [apps, filter]
  );

  // Emails already on the roster — a repeat sign-up is usually a parent adding
  // a second child, but it's worth flagging before creating a duplicate.
  const knownEmails = useMemo(
    () => new Set((players || []).map(p => String(p.email || "").trim().toLowerCase()).filter(Boolean)),
    [players]
  );

  async function handleApprove(app) {
    setBusyId(app.id);
    try {
      const player = applicationToPlayer(app, nextPlayerId(players));
      // Only mark the form approved once the member is actually saved; if the
      // save is refused the form stays in the queue to approve again.
      if (!(await addPlayers([player]))) {
        onNotify?.({ message: `${player.name} could not be added, so the sign-up is still waiting. Reload the page and approve it again.` });
        return;
      }
      await resolveApplication(app.id, "approved");
      setApps(prev => prev.map(a => (a.id === app.id ? { ...a, status: "approved" } : a)));
      setExpanded(null);
      onNotify?.({ message: `${player.name} added to the roster — assign them a group in Roster.` });
    } catch {
      onNotify?.({ message: "Could not update the sign-up on the server. Please try again." });
    } finally {
      setBusyId(null);
    }
  }

  async function handleDecline(app) {
    setBusyId(app.id);
    try {
      await resolveApplication(app.id, "declined");
      setApps(prev => prev.map(a => (a.id === app.id ? { ...a, status: "declined" } : a)));
      setExpanded(null);
    } catch {
      onNotify?.({ message: "Could not update the sign-up on the server. Please try again." });
    } finally {
      setBusyId(null);
    }
  }

  async function handleReopen(app) {
    setBusyId(app.id);
    try {
      await resolveApplication(app.id, "pending");
      setApps(prev => prev.map(a => (a.id === app.id ? { ...a, status: "pending" } : a)));
    } catch {
      onNotify?.({ message: "Could not update the sign-up on the server. Please try again." });
    } finally {
      setBusyId(null);
    }
  }

  if (apps === null) {
    return <p className="text-sm text-gray-500 p-6">Loading sign-ups…</p>;
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Sign-ups</h2>
          <p className="text-sm text-gray-500">
            Applications from the public form. Share the link{" "}
            <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs break-all">
              {JOIN_URL}
            </code>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={copyJoinLink}
            title={`Copy ${JOIN_URL} to the clipboard`}
            className={`text-sm border px-3 py-1.5 rounded-lg transition-colors ${
              copied
                ? "bg-green-600 border-green-600 text-white"
                : "border-gray-300 hover:bg-gray-50 text-gray-700"
            }`}
          >
            {copied ? "✓ Copied" : "⧉ Copy sign-up link"}
          </button>
          <button
            onClick={load}
            className="text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        {["pending", "approved", "declined"].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-sm px-3 py-1.5 rounded-lg border capitalize transition-colors ${
              filter === s
                ? "bg-blue-600 border-blue-600 text-white font-medium"
                : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {s} ({counts[s]})
          </button>
        ))}
      </div>

      {error && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          {error}
        </p>
      )}

      {visible.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <div className="text-3xl mb-2">📭</div>
          <p className="text-gray-500 text-sm">
            {filter === "pending"
              ? "No sign-ups waiting for review."
              : `Nothing ${filter}.`}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map(app => (
            <ApplicationRow
              key={app.id}
              app={app}
              expanded={expanded === app.id}
              busy={busyId === app.id}
              duplicate={knownEmails.has(String(app.email || "").trim().toLowerCase())}
              onToggle={() => setExpanded(expanded === app.id ? null : app.id)}
              onApprove={() => handleApprove(app)}
              onDecline={() => handleDecline(app)}
              onReopen={() => handleReopen(app)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ApplicationRow({ app, expanded, busy, duplicate, onToggle, onApprove, onDecline, onReopen }) {
  const name = `${app.firstName || ""} ${app.lastName || ""}`.trim() || "(no name)";
  const age = ageFromDob(app.dob);
  const colors = TIER_COLORS[app.tier] || TIER_COLORS["No Group"];
  const isJunior = app.classification === "Junior";
  const pending = (app.status || "pending") === "pending";

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-800">{name}</span>
            {age !== null && <span className="text-xs text-gray-500">age {age}</span>}
            <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} ${colors.text} ${colors.border} border`}>
              {app.tier || "No Group"}
            </span>
            {duplicate && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                email already on roster
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate">
            {app.ref} · {app.email} · {app.phone} ·{" "}
            {(app.availability || []).length} time
            {(app.availability || []).length === 1 ? "" : "s"} available
          </div>
        </div>
        <span className="text-gray-400 text-sm flex-shrink-0">{expanded ? "▲" : "▼"}</span>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-4 py-4 space-y-4 bg-gray-50">
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
            <Detail label="Classification" value={app.classification} />
            <Detail label="Gender" value={app.gender} />
            <Detail label="Date of birth" value={app.dob} />
            <Detail label="Submitted" value={formatDate(app.submittedAt)} />
            <Detail label="Email" value={app.email} />
            <Detail label="Mobile" value={app.phone} />
            <Detail label="Address" value={app.address} />
            <Detail label="Preferred contact" value={app.contactMethod} />
            <Detail label="Level notes" value={app.skillLevel} />
            <Detail label="Preferred partners" value={app.preferredPartners} />
            <Detail label="Private lessons" value={app.privateInterest ? "Interested" : "No"} />
          </div>

          {isJunior && (
            <div className="border-t border-gray-200 pt-3">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Parent / guardian & emergency
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                <Detail label="Guardian" value={app.guardianName} />
                <Detail label="Relationship" value={app.guardianRelationship} />
                <Detail label="Guardian mobile" value={app.guardianPhone} />
                <Detail label="Guardian email" value={app.guardianEmail} />
                <Detail label="Emergency contact" value={app.emergencyName} />
                <Detail label="Emergency phone" value={app.emergencyPhone} />
              </div>
            </div>
          )}

          <div className="border-t border-gray-200 pt-3">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Availability
            </div>
            {(app.availability || []).length === 0 ? (
              <p className="text-sm text-gray-400">None given</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {app.availability.map(slot => (
                  <span
                    key={slot}
                    className="text-xs bg-blue-50 border border-blue-200 text-blue-800 px-2 py-1 rounded"
                  >
                    {slot.replace("|", " ")}
                  </span>
                ))}
              </div>
            )}
          </div>

          {app.notes && (
            <div className="border-t border-gray-200 pt-3">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Notes
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{app.notes}</p>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            {pending ? (
              <>
                <button
                  onClick={onApprove}
                  disabled={busy}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
                >
                  {busy ? "Working…" : "Approve & add to roster"}
                </button>
                <button
                  onClick={onDecline}
                  disabled={busy}
                  className="flex-1 border border-gray-300 hover:bg-white disabled:opacity-50 text-gray-600 text-sm font-medium py-2 rounded-lg transition-colors"
                >
                  Decline
                </button>
              </>
            ) : (
              <button
                onClick={onReopen}
                disabled={busy}
                className="border border-gray-300 hover:bg-white disabled:opacity-50 text-gray-600 text-sm font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Move back to pending
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
      <div className="text-sm text-gray-800 break-words">{value || "—"}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
